<template>
  <ion-app>
    <!-- Banner offline GLOBALE (position:fixed): visibile su ogni vista e su
         ogni runtime, incluse le pagine pushate a schermo intero e il web/PWA.
         I banner GPS restano montati in MapView (contesto in cui servono). -->
    <MobileHealthBanner :show-location="false" />
    <ion-router-outlet />
  </ion-app>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, type WatchStopHandle } from 'vue';
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import MobileHealthBanner from '@/components/common/MobileHealthBanner.vue';
import { useMobileHealthStore } from '@/stores/useMobileHealthStore';

const router = useRouter();
// Monitoraggio rete/GPS a livello APP (prima viveva in TabsLayout: usciva di
// scena su logout/viste fuori dai tabs). start() è idempotente.
const mobileHealthStore = useMobileHealthStore();
let stopPasswordRecoveryWatch: WatchStopHandle | null = null;

function recoverStuckRouterInteractivity() {
  if (typeof document === 'undefined') return;

  // Dopo un lungo background la WebView Android può riprendere con elementi
  // Ionic "congelati": pointer-events:none su body/app, pagine attive rimaste
  // .ion-page-invisible, o backdrop orfani (di overlay già chiusi) che
  // catturano ogni tap rendendo morti cambio-vista e pulsanti.
  for (const el of [document.body, ...Array.from(document.querySelectorAll('ion-app'))] as HTMLElement[]) {
    if (el && el.style.pointerEvents === 'none') el.style.pointerEvents = '';
  }
  const hasOpenOverlay = !!document.querySelector(
    'ion-modal.show-modal, ion-alert.show-alert, ion-action-sheet.show-action-sheet, ion-popover.show-popover, ion-loading.show-loading'
  );
  if (!hasOpenOverlay) {
    for (const backdrop of Array.from(document.querySelectorAll('ion-backdrop'))) {
      backdrop.remove();
    }
  }

  const outlets = Array.from(document.querySelectorAll('ion-router-outlet')) as HTMLElement[];
  for (const outlet of outlets) {
    if (outlet.style.pointerEvents === 'none') {
      outlet.style.pointerEvents = '';
    }

    // Solo figli DIRETTI dell'outlet (vedi runIonRecovery: le pagine annidate
    // dei tab NON vanno toccate, altrimenti si nasconde il contenitore tabs).
    const pages = (Array.from(outlet.children) as HTMLElement[])
      .filter((el) => el.classList?.contains('ion-page'));
    if (!pages.length) continue;

    const activePage = [...pages].reverse().find((page) => !page.classList.contains('ion-page-hidden')) ?? pages[pages.length - 1];
    if (!activePage) continue;

    if (activePage.classList.contains('ion-page-invisible')) {
      activePage.classList.remove('ion-page-invisible');
    }
    if (activePage.style.pointerEvents === 'none') {
      activePage.style.pointerEvents = 'auto';
    }
    // Re-nascondi le pagine stantie (stessa logica di runIonRecovery): senza,
    // al resume la vista precedente può restare visibile sopra quella attiva.
    for (const page of pages) {
      if (page !== activePage && !page.classList.contains('ion-page-hidden')) {
        page.classList.add('ion-page-hidden');
      }
    }
  }
}

function onVisibilityResume() {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  requestAnimationFrame(() => {
    recoverStuckRouterInteractivity();
    window.dispatchEvent(new Event('resize'));
    window.dispatchEvent(new Event('swapwish:app-foreground'));
  });
}

// Su Android la WebView spesso NON emette `visibilitychange`/`focus` al ritorno
// da un lungo background: il segnale affidabile è il plugin nativo Capacitor
// App (resume / appStateChange.isActive). Senza questo la UI resta "morta"
// (cambio vista e pulsanti non rispondono) perché il recovery non scatta mai.
const capacitorAppListeners: PluginListenerHandle[] = [];

// Deep-link di autenticazione (App Links): la conferma email / il recupero
// password / l'OAuth, invece di aprire il browser, aprono direttamente l'app con
// l'URL https://www.swapwishtcg.com/auth/callback?... → qui si completa la
// sessione (scambio del code PKCE) e si entra in app, senza sessione browser.
async function handleAppUrlOpen(rawUrl: string): Promise<void> {
  if (!rawUrl) return;
  let url: URL;
  try { url = new URL(rawUrl); } catch { return; }
  const params = url.searchParams;
  const hashParams = new URLSearchParams(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);

  // Deep-link invito referral (Fase 3): cattura il codice da /invite/<code> o dai
  // parametri referral_code/ref (compatibile con i link Branch). Lo conserva e,
  // se c'è già una sessione, lo applica; altrimenti verrà applicato al post-auth.
  const inviteMatch = url.pathname.match(/\/invite\/([^/?#]+)/i);
  const inviteCode = inviteMatch?.[1] || params.get('referral_code') || params.get('ref');
  if (inviteCode) {
    const { referralService } = await import('@/services/referralService');
    referralService.savePendingReferralCode(decodeURIComponent(inviteCode).trim());
    void referralService.applyPendingReferralCode().catch(() => { /* riprovato al post-auth */ });
    return; // non è un link di auth: niente scambio sessione/redirect forzato
  }

  // Deep-link profilo condiviso: https://www.swapwishtcg.com/s/<token> con app
  // installata apre DIRETTAMENTE la vista profilo in-app (routing ad-hoc),
  // invece di restare nel browser.
  const shareMatch = url.pathname.match(/^\/s\/([^/?#]+)/i);
  if (shareMatch?.[1]) {
    await router.push(`/s/${encodeURIComponent(decodeURIComponent(shareMatch[1]))}`).catch(() => {});
    return;
  }

  const code = params.get('code');
  const isAuthLink = /\/auth\/(callback|reset-password)/.test(url.pathname)
    || !!code || hashParams.has('access_token');
  if (!isAuthLink) return; // deep-link non di auth: lasciali al router/altro

  const isRecovery = params.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
  // Chiude il Custom Tab dell'OAuth Google (aperto da authService su nativo):
  // al ritorno via App Link il browser di sistema resta in primo piano.
  if (Capacitor.isNativePlatform()) {
    void import('@capacitor/browser')
      .then(({ Browser }) => Browser.close())
      .catch(() => { /* nessun browser aperto */ });
  }
  const { supabase } = await import('@/services/supabaseClient');
  try {
    if (code) {
      // Flow PKCE: scambio del code per la sessione.
      await supabase.auth.exchangeCodeForSession(code);
    } else {
      // Flow implicito (es. recupero password): i token arrivano nell'hash del
      // deep-link e NON vengono auto-rilevati (l'URL non è la location della
      // WebView) → si imposta la sessione manualmente coi token dell'hash.
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      if (accessToken && refreshToken) {
        await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
      }
    }
  } catch (e) {
    console.warn('[appUrlOpen] session establishment failed', e);
  }
  const { data } = await supabase.auth.getSession();
  await router.replace(data.session ? (isRecovery ? '/auth/reset-password' : '/loading') : '/login');
}

onMounted(() => {
  mobileHealthStore.start();
  void import('@/stores/useUserStore').then(({ useUserStore }) => {
    const userStore = useUserStore();
    // Recupero password: se l'evento PASSWORD_RECOVERY scatta DOPO che la
    // navigazione iniziale si è già assestata, forza la vista di reset.
    stopPasswordRecoveryWatch = watch(() => userStore.passwordRecovery, (active) => {
      if (active && router.currentRoute.value.path !== '/auth/reset-password') {
        void router.replace('/auth/reset-password');
      }
    });
  }).catch((e) => console.warn('[password recovery watch]', e));

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityResume);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onVisibilityResume);
    window.addEventListener('pageshow', onVisibilityResume);
  }
  if (Capacitor.isNativePlatform()) {
    // Aggiornamento forzato (Android In‑App Update immediate): se sul Play Store
    // c'è una versione più recente, blocca l'app sul flusso di update finché non
    // si aggiorna. No‑op su build non‑Play (debug/sideload).
    void import('@/services/appUpdateService')
      .then(({ checkForImmediateAppUpdate }) => checkForImmediateAppUpdate())
      .catch((e) => console.warn('[app update check]', e));

    // Deferred deep link referral (Android): se l'app è stata installata da un
    // link Play Store con &referrer=ref%3D<code>, recupera il codice al primo
    // avvio. No‑op su iOS (nessun Install Referrer nativo) e dopo la prima lettura.
    void import('@/services/installReferrerService')
      .then(({ installReferrerService }) => installReferrerService.captureOnce())
      .catch((e) => console.warn('[install referrer]', e));

    void import('@capacitor/app').then(({ App: CapacitorApp }) => {
      void CapacitorApp.addListener('resume', () => onVisibilityResume())
      .then((handle) => capacitorAppListeners.push(handle))
      .catch((e) => console.warn('[app resume listener]', e));
      void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        onVisibilityResume();
      } else {
        // App in background/chiusura: salva lo snapshot dei candidati vicini
        // come baseline, così lo scan notificherà solo quelli NUOVI comparsi
        // dopo la chiusura. (Nessun-op su web / utenti non Pro.)
        void import('@/services/nearbyAlertsService')
          .then(({ snapshotNearbyBaseline }) => snapshotNearbyBaseline())
          .catch(() => { /* noop */ });
      }
    })
      .then((handle) => capacitorAppListeners.push(handle))
      .catch((e) => console.warn('[app state listener]', e));
    void CapacitorApp.addListener('appUrlOpen', (event) => { void handleAppUrlOpen(event.url); })
      .then((handle) => capacitorAppListeners.push(handle))
      .catch((e) => console.warn('[app url open listener]', e));
    // 'pause' è l'evento Android canonico di passaggio in background: precede
    // sempre la terminazione manuale (swipe dai recenti). Doppio aggancio con
    // appStateChange per massima copertura dello snapshot baseline alla chiusura.
      void CapacitorApp.addListener('pause', () => {
        void import('@/services/nearbyAlertsService')
          .then(({ snapshotNearbyBaseline }) => snapshotNearbyBaseline())
          .catch(() => { /* noop */ });
      })
      .then((handle) => capacitorAppListeners.push(handle))
      .catch((e) => console.warn('[app pause listener]', e));
    }).catch((e) => console.warn('[capacitor app listeners]', e));
  }
});

onUnmounted(() => {
  mobileHealthStore.stop();
  stopPasswordRecoveryWatch?.();
  stopPasswordRecoveryWatch = null;
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityResume);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('focus', onVisibilityResume);
    window.removeEventListener('pageshow', onVisibilityResume);
  }
  for (const handle of capacitorAppListeners.splice(0)) {
    void handle.remove().catch(() => { /* noop */ });
  }
});
</script>

<style scoped>
</style>
